import React, { useMemo } from 'react'

import { TableCol } from '../../../../../components/pdf/Table'
import Separator from '../../../../../components/pdf/Separator'
import Text from '../../../../../components/pdf/Text'
import View from '../../../../../components/pdf/View'
import { buildCargoInventario, agruparCargosPorSetor } from '../_helpers/cargoEngine'
import { FALLBACK } from '../../_helpers/pdfHelpers'
import { HEADING_COLOR, TABLE_BORDER_RED } from '../_helpers/brand'
import { RedDataCell, RedHeadCell, RedRow, RedTable } from './_shared/BradescoTable'
import { LANDSCAPE_PAGE_STYLE, LandscapePageDefinition } from './_shared/Landscape'

// Texto fixo institucional da caixa "Composição do GHE" — idêntico em toda ocorrência do
// modelo real (páginas 42-51, uma caixa por Setor/Cargo, antes da tabela de riscos daquele
// grupo). Não é campo dinâmico.
const COMPOSICAO_GHE_TEXTO =
  'Os riscos físicos, químicos, biológicos e ergonômicos/biomecânicos possuem Grupo Homogêneo de Exposição (GHE) ' +
  'definido por setor, podendo abranger um ou mais cargos. Para os riscos ergonômicos de natureza psicossocial, o ' +
  'GHE é estabelecido por cargo.'

/**
 * Seção 18 - Inventário de Riscos. Uma sub-tabela por GHE — confirmado contra o modelo
 * de referência real (56 páginas, Seção 18 nas páginas 42-51) que o agrupamento é
 * sempre por CARGO, nunca por Setor: dois cargos do mesmo Setor (ex.: "Gerencia
 * Negócios e Atendimento" e "Supervisor Administrativo", ambos em "Gerência
 * Administrativa") geram dois blocos cabeçalho+tabela separados, nunca combinados numa
 * única tabela. Cada tabela tem sempre a MESMA lista fixa de 13 linhas de risco, nesta
 * ordem (`_helpers/inventarioRiscosFixo.js`) — nunca omitida/reordenada por falta de
 * resposta da atividade; só Nº func. expostos (por Cargo) e, por linha,
 * Probabilidade/Severidade/Nível de Risco variam com os dados.
 *
 * A associação de cada resposta ao seu Cargo é resolvida subindo a árvore
 * `parentReference` a partir da posição de Cargo (ver `_helpers/cargoEngine.js`,
 * `getDescendantQuestionIds`). O campo "Setor:" desta caixa (`cargo.setor`) já não é
 * mais o único Setor global repetido em toda caixa — é o Setor estruturalmente
 * associado a ESTE Cargo especificamente (pergunta-marcador "Tire foto... do setor [X]"
 * mais próxima antes dele na ordem de exibição da checklist, ver
 * `getSetorPorPosicaoDeCargo` em `_helpers/cargoEngine.js` para a evidência completa —
 * confirmado por vídeo da tela real do builder/preview do checklist do Bradesco,
 * 2026-09-01), com fallback para o Setor global só quando a estrutura não resolve nada.
 *
 * Orientação paisagem: confirmado no PDF-modelo (páginas 42-51) — ver `_shared/Landscape.js`
 * para o mecanismo (CSS Paged Media, sem tocar em `components/pdf/`) e a limitação de
 * validação visual.
 *
 * Cabeçalho da tabela e caixa "GHE": corrigidos por inspeção pixel-a-pixel das páginas
 * 42-51 do modelo real (antes: cabeçalho em uma única linha com a coluna combinada "GHE
 * Cargo", e um título solto "{setor} — {cargo}" sem a caixa institucional). O modelo real
 * usa cabeçalho em duas linhas — "GHE" (colspan 1) e "Classificação do Risco" (colspan 3,
 * sub-colunas "Prob." / "Sev." / "Nível Risco") — e, antes de cada sub-tabela, uma caixa de
 * borda vermelha com "Grupo Homogêneos de Exposição (GHE)" / "Setor:" / "Cargo:" à esquerda
 * e o texto fixo "Composição do GHE:" à direita.
 *
 * Cabeçalho repetido em quebra de página (as duas RedRow abaixo, dentro de um `<thead>`
 * com `display: table-header-group`): o modelo real (arquivo de referência, 56 páginas)
 * NÃO repete cabeçalho de tabela em nenhuma página — no PDF original o autor reinsere o
 * cabeçalho manualmente a cada novo bloco paisagem. `pgr_2026_input_contract.json` pede
 * essa repetição de forma explícita (`options.repeatRiskInventoryHeaderOnPageBreak: true`)
 * e `PGR_2026_ESPECIFICACAO_TECNICA.md` (seção H, tabela `repeatTableHeader`) recomenda
 * habilitá-la especificamente nesta tabela ("T17") — e em nenhuma outra — justamente porque,
 * diferente do PDF-modelo estático (onde cada bloco paisagem cabe numa página e o cabeçalho é
 * reinserido manualmente pelo autor), aqui as 13 linhas fixas de cada Cargo (algumas com texto
 * longo em várias colunas) podem quebrar página sem que nenhum cabeçalho tenha sido reinserido
 * manualmente. Igual
 * ao contador de página do rodapé (`Footer.js`) e à orientação paisagem (`Landscape.js`),
 * `display: table-header-group` é um recurso do motor de impressão do Chromium — não foi
 * possível validar visualmente neste ambiente (sem navegador para gerar o PDF real).
 *
 * Agrupamento por Setor (2026-09-03, regra definitiva — ver `agruparCargosPorSetor` em
 * `_helpers/cargoEngine.js` para a causa raiz completa): os Cargos são agrupados por
 * Setor (fonte combinada das perguntas 256 e 354, `mappingIds.js`) antes de renderizar,
 * garantindo que cada Setor respondido tenha seus Cargos reunidos e corretamente
 * rotulados — nunca um Cargo de um Setor exibindo o rótulo de outro Setor (ou de todos os
 * Setores concatenados, o bug real que motivou esta correção). O agrupamento é só de
 * ORDEM/dados: cada Cargo continua com sua própria caixa+tabela completa, exatamente como
 * já confirmado pixel-a-pixel contra o PDF-modelo (nenhum título de seção novo por Setor
 * foi adicionado — ver ABERTO em `agruparCargosPorSetor`).
 */
const InventarioRiscosSection = ({ serviceOrder, assets }) => {
  const cargos = useMemo(() => buildCargoInventario(serviceOrder.replies, assets), [serviceOrder.replies, assets])
  const gruposPorSetor = useMemo(() => agruparCargosPorSetor(cargos), [cargos])

  return (
    <View pageBreakAfter id="18" style={LANDSCAPE_PAGE_STYLE}>
      <LandscapePageDefinition />
      <Separator weight={15} />
      <Text bold size="small" color={HEADING_COLOR}>
        18. INVENTÁRIO DE RISCOS
      </Text>
      <Separator weight={8} />

      {gruposPorSetor.flatMap(grupo => grupo.cargos).map(cargo => (
        <View key={cargo.index} pageBreakAfter>
          <RedTable width="100%">
            <RedRow>
              <TableCol color={TABLE_BORDER_RED} width="35%" style={{ verticalAlign: 'top' }}>
                <Text bold size="xsmall">
                  Grupo Homogêneos de Exposição (GHE)
                </Text>
                <Text size="xsmall">
                  <Text bold inline size="xsmall">
                    Setor:
                  </Text>{' '}
                  {cargo.setor}
                </Text>
                <Text size="xsmall">
                  <Text bold inline size="xsmall">
                    Cargo:
                  </Text>{' '}
                  {cargo.cargo}
                </Text>
              </TableCol>
              <TableCol color={TABLE_BORDER_RED} style={{ verticalAlign: 'top' }}>
                <Text size="xsmall">
                  <Text bold inline size="xsmall">
                    Composição do GHE:
                  </Text>{' '}
                  {COMPOSICAO_GHE_TEXTO}
                </Text>
              </TableCol>
            </RedRow>
          </RedTable>
          <Separator weight={6} />

          <View style={{ overflowX: 'auto' }}>
            <RedTable width="100%" fixedLayout>
              <thead style={{ display: 'table-header-group' }}>
                <RedRow>
                  <RedHeadCell size="xsmall" rowSpan={2}>
                    Possível Risco
                  </RedHeadCell>
                  <RedHeadCell size="xsmall" rowSpan={2}>
                    Tipo de exposição
                  </RedHeadCell>
                  <RedHeadCell size="xsmall" rowSpan={2}>
                    Fonte geradora
                  </RedHeadCell>
                  <RedHeadCell size="xsmall" rowSpan={2}>
                    Nº func. expostos
                  </RedHeadCell>
                  <RedHeadCell size="xsmall" rowSpan={2}>
                    Possíveis lesões/agravos
                  </RedHeadCell>
                  <RedHeadCell size="xsmall" rowSpan={2}>
                    Embasam. Técnico
                  </RedHeadCell>
                  <RedHeadCell size="xsmall">GHE</RedHeadCell>
                  <RedHeadCell size="xsmall" colSpan={3}>
                    Classificação do Risco
                  </RedHeadCell>
                  <RedHeadCell size="xsmall" rowSpan={2}>
                    Medidas de controle existentes
                  </RedHeadCell>
                </RedRow>
                <RedRow>
                  <RedHeadCell size="xsmall">Cargo</RedHeadCell>
                  <RedHeadCell size="xsmall">Prob.</RedHeadCell>
                  <RedHeadCell size="xsmall">Sev.</RedHeadCell>
                  <RedHeadCell size="xsmall">Nível Risco</RedHeadCell>
                </RedRow>
              </thead>

              {cargo.linhas.map((linha, i) => (
                <RedRow key={i} striped={i % 2 === 1}>
                  <RedDataCell align="left" size="xsmall">
                    {linha.possivelRisco}
                  </RedDataCell>
                  <RedDataCell align="left" size="xsmall" preLine>
                    {linha.tipoExposicao}
                  </RedDataCell>
                  <RedDataCell align="left" size="xsmall" preLine>
                    {linha.fonteGeradora}
                  </RedDataCell>
                  <RedDataCell align="center" size="xsmall">
                    {cargo.numFuncionariosExpostos}
                  </RedDataCell>
                  <RedDataCell align="left" size="xsmall" preLine>
                    {linha.possiveisLesoes}
                  </RedDataCell>
                  <RedDataCell align="left" size="xsmall" preLine>
                    {linha.embasamentoTecnico}
                  </RedDataCell>
                  <RedDataCell align="left" size="xsmall">
                    {cargo.cargo}
                  </RedDataCell>
                  <RedDataCell align="center" size="xsmall">
                    {linha.probabilidadeTexto ?? FALLBACK}
                  </RedDataCell>
                  <RedDataCell align="center" size="xsmall">
                    {linha.severidadeTexto ?? FALLBACK}
                  </RedDataCell>
                  <RedDataCell align="center" size="xsmall">
                    {linha.nivelDeRisco}
                  </RedDataCell>
                  <RedDataCell align="left" size="xsmall" preLine>
                    {linha.medidasControle}
                  </RedDataCell>
                </RedRow>
              ))}
            </RedTable>
          </View>
          <Separator weight={10} />
        </View>
      ))}

      <Separator weight={15} />
    </View>
  )
}

export default InventarioRiscosSection
